import { ApolloClient as FlexDataClient } from "@apollo/client/core";
import type { TypePolicies } from "@apollo/client/core";
import { LocalState } from "@apollo/client/local-state";
import { HttpLink } from "@apollo/client/link/http";
import { RetryLink } from "@apollo/client/link/retry";
import { ApolloLink } from "@apollo/client/link";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { SetContextLink } from "@apollo/client/link/context";
import { createClient } from "graphql-ws";
import { WebSocket } from "ws";
import { Kind, OperationTypeNode, print } from "graphql";
import { DataClientError } from "../error";
import { cache } from "./cache";
import { getEnvironmentConfig } from "~/modules/config";
import { buildRegionalHost } from "~/utils/regionUtil";




export interface DataClientOptions {
    connectToDevTools?: boolean;
    localSchema?: {
        resolvers: LocalState.Resolvers | LocalState.Resolvers[];
    };
    errorLink?: ApolloLink;
    typePolicies?: TypePolicies;
}




export type GetToken = () => Promise<string> | string;




export interface CreateDataClientOptions extends DataClientOptions {
    region?: string;
}




















export const createDataClient = async (tokenOrGetToken: string | GetToken, options?: CreateDataClientOptions) => {
    const MAX_HEADER_SIZE = 8000; 

    try {
        const environmentConfig = getEnvironmentConfig();
        const { region } = environmentConfig ?? {};
        const regionToUse = buildRegionalHost(region ?? options?.region ?? "");
        const graphQLSubscriptionUri = `wss://event-bridge${regionToUse}.twilio.com/graphql-subscriptions`;
        const graphQLApiUri = `https://preview${regionToUse}.twilio.com/Flex/graphql`;
        const tokenIsFunction = typeof tokenOrGetToken === "function";

        const links = [];
        if (options?.errorLink) {
            links.push(options.errorLink);
        }

        const authLink = new SetContextLink(async (prevContext) => {
            const authLinkToken = tokenIsFunction ? await tokenOrGetToken() : tokenOrGetToken;
            const auth = btoa(`token:${authLinkToken}`);

            return {
                headers: {
                    ...(prevContext.headers as Record<string, unknown>),
                    authorization: `Basic ${auth}`
                }
            };
        });
        links.push(authLink);

        const retryLink = new RetryLink({
            attempts: {
                max: 5
            },
            delay: {
                initial: 1000,
                max: 3000
            }
        });
        links.push(retryLink);

        const dataClientHttpLink = () => {
            return new ApolloLink((operation, forward) => {
                const { headers } = operation.getContext();
                const query = print(operation.query);
                const headerSize = JSON.stringify(headers).length;
                const variables = JSON.stringify(operation.variables);
                const fullUrlSize = `${graphQLApiUri}?query=${encodeURIComponent(query)}&variables=${encodeURIComponent(
                    variables
                )}`.length;

                
                
                const useGET = fullUrlSize + headerSize <= MAX_HEADER_SIZE;

                const httpLink = new HttpLink({
                    uri: graphQLApiUri,
                    useGETForQueries: useGET,
                    enhancedClientAwareness: { transport: false }
                });
                return httpLink.request(operation, forward);
            });
        };
        links.push(dataClientHttpLink()); 

        const wsUri = graphQLSubscriptionUri;
        const wsLink = new GraphQLWsLink(
            createClient({
                url: wsUri,
                
                ...(typeof WebSocket === "undefined" && { webSocketImpl: WebSocket }),
                connectionParams: async () => ({
                    authToken: `Bearer ${tokenIsFunction ? await tokenOrGetToken() : tokenOrGetToken}`
                })
            })
        );

        const splitLink = ApolloLink.split(
            ({ query }) => {
                const definition = getMainDefinition(query);
                return (
                    definition.kind === Kind.OPERATION_DEFINITION &&
                    definition.operation === OperationTypeNode.SUBSCRIPTION
                );
            },
            wsLink,
            ApolloLink.from(links)
        );

        if (options?.typePolicies) {
            cache.policies.addTypePolicies(options?.typePolicies);
        }

        const client = new FlexDataClient({
            cache,
            devtools: { enabled: options?.connectToDevTools ?? false },
            link: splitLink,
            defaultOptions: {
                query: {
                    fetchPolicy: "network-only", 
                    errorPolicy: "all" 
                }
            },
            ...(options?.localSchema?.resolvers && {
                localState: new LocalState({
                    resolvers: (Array.isArray(options.localSchema.resolvers)
                        ? Object.assign({}, ...options.localSchema.resolvers)
                        : options.localSchema.resolvers) as LocalState.Resolvers
                })
            })
        });
        return Promise.resolve(client);
    } catch (error) {
        throw new Error(DataClientError.GraphqlInitializationFailed, { cause: error });
    }
};
